import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faBoxesStacked,
	faClockRotateLeft,
	faFileInvoiceDollar,
	faStore,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { StorefrontDashboard } from '../components/dashboard/StorefrontDashboard';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { getStorefrontDashboard } from '../services/portalDashboardService';

interface Tile {
	key: string;
	label: string;
	icon: IconDefinition;
	path: string;
	visible: boolean;
}

/**
 * Action-tile dashboard for storefronts whose admin set
 * `defaultAuthenticatedView='dashboard'`. Tiles are filtered by the same
 * display flags that hide nav links elsewhere — no need to render a
 * "View Orders" tile if order history is disabled for this storefront.
 */
export function DashboardPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { profile, activeClient, token } = usePortalAuth();
	const display = useDisplaySettings();

	// First check if a STOREFRONT-scope dashboard design is assigned to
	// the active portal_site (admin authored via CRM Settings →
	// Storefront → Dashboard Designs). If so, render that instead of
	// the action-tile fallback. Single lightweight query — the
	// dashboard component does its own fetching after.
	const designProbe = useQuery({
		queryKey: ['portal', 'dashboard', 'probe'],
		queryFn: () => getStorefrontDashboard(token),
		staleTime: 60_000,
	});
	const hasStorefrontDesign =
		designProbe.data?.data != null &&
		(designProbe.data.data.widgets?.length ?? 0) > 0;
	if (hasStorefrontDesign) {
		return <StorefrontDashboard />;
	}
	// Hold off on the action-tile fallback until the probe has
	// definitively returned (success OR failure). Without this gate,
	// the very first render would land on the fallback "Browse
	// Products / Account" cards and then flicker into <StorefrontDashboard>
	// 1–3 seconds later when the probe resolves — a visible content
	// swap that read as the page reloading. A neutral splash for the
	// in-flight window keeps the route stable until we actually know
	// which dashboard to render.
	if (designProbe.isLoading) {
		return (
			<div className="px-4 py-6 max-w-7xl mx-auto">
				<div className="animate-pulse space-y-4">
					<div className="h-32 bg-brand-input rounded-xl" />
					<div className="h-48 bg-brand-input rounded-xl" />
				</div>
			</div>
		);
	}

	const tiles: Array<Tile> = [
		{
			key: 'browse',
			label: 'Browse Products',
			icon: faStore,
			path: '/catalog',
			visible: display.canBrowseCatalog,
		},
		{
			key: 'cart',
			label: 'Cart',
			icon: faBoxesStacked,
			path: '/cart',
			visible: display.canPurchase,
		},
		{
			key: 'orders',
			label: 'Order History',
			icon: faClockRotateLeft,
			path: '/orders',
			visible: display.canViewOrderHistory,
		},
		{
			key: 'invoices',
			label: 'Invoices',
			icon: faFileInvoiceDollar,
			path: '/invoices',
			visible: display.canViewInvoices,
		},
		{
			key: 'account',
			label: 'Account',
			icon: faUser,
			path: '/account',
			visible: true,
		},
	];

	const visibleTiles = tiles.filter((t) => t.visible);

	return (
		<div className="px-4 py-6 max-w-3xl mx-auto">
			<div className="mb-6">
				<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
					{t('Welcome')}
					{profile != null && profile.firstName.length > 0
						? `, ${t(profile.firstName)}`
						: ''}
				</h1>
				{activeClient != null && (
					<p className="text-sm text-brand-muted mt-1">
						{t(activeClient.name)}
					</p>
				)}
			</div>

			<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
				{visibleTiles.map((tile) => (
					<button
						key={tile.key}
						type="button"
						onClick={() => navigate(tile.path)}
						className="flex flex-col items-center justify-center gap-2 aspect-square bg-brand-surface border border-brand-border rounded-xl p-4 hover:shadow-md transition-shadow"
					>
						<FontAwesomeIcon
							icon={tile.icon}
							className="text-2xl text-portal-accent"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center">
							{t(tile.label)}
						</span>
					</button>
				))}
			</div>
		</div>
	);
}
